/**
 * Existing Zuora business accounts on the business page (migration 2026-10-19-581).
 *  - Which Zuora "BUSINESS ACCOUNT" (BusinessAccountId on member accounts) belongs to this organization: linked by an
 *    administrator (search by name), or automatically when the organization carries that Zuora account id.
 *  - Members found through that linkage (read-only, derived from Zuora), with the two candidate "comp" counts -- the
 *    comp rule is not decided yet, so both are shown and neither is assumed.
 *  - The comp-seat allotment, ENTERED BY STAFF (admin / manager), kept as history (entries are never edited).
 * For an organization with no linked business account the card is shown only to administrators (to link one).
 */

import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Card from "../ui/Card";
import Button from "../common/Button";
import { useToast } from "../ui/Toast";
import { formatDateMDY } from "../../utils/formatters";
import {
  addCompAllotment,
  friendlyRpcError,
  getBusinessAccountMembers,
  getBusinessAccountSummary,
  linkBusinessAccount,
  searchBusinessAccounts,
} from "../../services/sponsorship.service";

interface Props {
  organizationId: string;
  canLink: boolean; // superadmin / admin
  canSetAllotment: boolean; // superadmin / admin / manager
}

const todayCT = (): string =>
  new Date().toLocaleDateString("en-CA", { timeZone: "America/Chicago" });

const BusinessCompAllotmentPanel: React.FC<Props> = ({
  organizationId,
  canLink,
  canSetAllotment,
}) => {
  const qc = useQueryClient();
  const { showToast } = useToast();
  const summary = useQuery({
    queryKey: ["business-account-summary", organizationId],
    queryFn: () => getBusinessAccountSummary(organizationId),
  });
  const [showMembers, setShowMembers] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const members = useQuery({
    queryKey: ["business-account-members", organizationId],
    queryFn: () => getBusinessAccountMembers(organizationId),
    enabled: showMembers,
  });
  const [linking, setLinking] = useState(false);
  const [q, setQ] = useState("");
  const search = useQuery({
    queryKey: ["business-account-search", q],
    queryFn: () => searchBusinessAccounts(q),
    enabled: linking && q.trim().length >= 2,
  });
  const [editing, setEditing] = useState(false);
  const [seats, setSeats] = useState("");
  const [effective, setEffective] = useState(todayCT());
  const [reason, setReason] = useState("");
  const [err, setErr] = useState<string | null>(null);

  const invalidate = () => {
    qc.invalidateQueries({
      queryKey: ["business-account-summary", organizationId],
    });
    qc.invalidateQueries({
      queryKey: ["business-account-members", organizationId],
    });
    qc.invalidateQueries({ queryKey: ["business-account-search"] });
  };
  const link = useMutation({
    mutationFn: (args: { id: string; org: string | null }) =>
      linkBusinessAccount(args.id, args.org),
    onSuccess: (_d, args) => {
      invalidate();
      setLinking(false);
      setQ("");
      showToast(
        "success",
        args.org ? "Business account linked" : "Business account unlinked",
      );
    },
    onError: (e: Error) => showToast("error", friendlyRpcError(e)),
  });
  const save = useMutation({
    mutationFn: () =>
      addCompAllotment(organizationId, Number(seats), effective, reason),
    onSuccess: () => {
      invalidate();
      setEditing(false);
      setSeats("");
      setReason("");
      setErr(null);
      showToast("success", "Comp allotment saved");
    },
    onError: (e: Error) => setErr(friendlyRpcError(e)),
  });

  const s = summary.data;
  if (summary.isLoading || summary.isError || !s) {
    return null;
  }
  const linked = s.business_accounts.length > 0;
  if (!linked && !canLink) {
    return null;
  }
  const allotted = s.allotment?.seats ?? null;
  const over =
    allotted !== null && s.active_comp_either > allotted
      ? s.active_comp_either - allotted
      : 0;
  const seatsValid = /^\d{1,6}$/.test(seats.trim()) && Number(seats) <= 100000;

  return (
    <Card>
      <div
        className="flex flex-wrap items-center justify-between gap-2 mb-2"
        data-testid="business-account-panel"
      >
        <h3 className="text-lg font-semibold text-gray-900">
          Zuora business account
        </h3>
        {canLink && !linking && (
          <button
            type="button"
            className="text-sm text-primary-600 hover:underline"
            onClick={() => setLinking(true)}
          >
            {linked ? "Link another" : "Link a business account"}
          </button>
        )}
      </div>

      {!linked && !linking && (
        <p className="text-sm text-gray-600">
          Not linked. Link the Zuora “BUSINESS ACCOUNT” this organization runs
          to see its members and track comp seats.
        </p>
      )}

      {linking && (
        <div className="mb-3 rounded border border-gray-200 p-3">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Search Zuora business accounts by name
          </label>
          <input
            className="w-full rounded border-gray-300 text-sm"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="e.g. Hamilton ISD"
            data-testid="business-account-search"
          />
          {q.trim().length >= 2 && (
            <ul className="mt-2 divide-y text-sm">
              {(search.data ?? []).map((r) => (
                <li
                  key={r.business_account_id}
                  className="py-1.5 flex flex-wrap items-center justify-between gap-2"
                >
                  <span>
                    {r.name || r.business_account_id}{" "}
                    <span className="text-xs text-gray-500">
                      {r.member_accounts} member account
                      {r.member_accounts === 1 ? "" : "s"}
                      {r.organization_name
                        ? ` · linked to ${r.organization_name}`
                        : ""}
                    </span>
                  </span>
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={
                      link.isPending || r.organization_id === organizationId
                    }
                    onClick={() =>
                      link.mutate({
                        id: r.business_account_id,
                        org: organizationId,
                      })
                    }
                  >
                    {r.organization_id === organizationId
                      ? "Linked"
                      : r.organization_id
                        ? "Move here"
                        : "Link"}
                  </Button>
                </li>
              ))}
              {search.data && search.data.length === 0 && (
                <li className="py-1.5 text-gray-500">No match.</li>
              )}
            </ul>
          )}
          <button
            type="button"
            className="mt-2 text-sm text-gray-600 hover:underline"
            onClick={() => {
              setLinking(false);
              setQ("");
            }}
          >
            Cancel
          </button>
        </div>
      )}

      {linked && (
        <>
          <ul className="text-sm mb-3">
            {s.business_accounts.map((b) => (
              <li
                key={b.business_account_id}
                className="flex flex-wrap items-center gap-2"
              >
                <span className="font-medium">
                  {b.name || b.business_account_id}
                </span>
                <span className="text-xs text-gray-500">
                  {b.link_source === "staff"
                    ? "linked by staff"
                    : "linked automatically"}
                </span>
                {canLink && (
                  <button
                    type="button"
                    className="text-xs text-red-600 hover:underline"
                    onClick={() =>
                      link.mutate({ id: b.business_account_id, org: null })
                    }
                  >
                    Unlink
                  </button>
                )}
              </li>
            ))}
          </ul>
          <dl
            className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm mb-3"
            data-testid="business-account-counts"
          >
            <div>
              <dt className="text-gray-500">Active members</dt>
              <dd className="text-lg font-semibold">{s.active_members}</dd>
            </div>
            <div>
              <dt className="text-gray-500">Comp (Comp/Employee type)</dt>
              <dd className="text-lg font-semibold">{s.active_comp_type}</dd>
            </div>
            <div>
              <dt className="text-gray-500">$0 per month</dt>
              <dd className="text-lg font-semibold">{s.active_zero_mrr}</dd>
            </div>
            <div>
              <dt className="text-gray-500">Comp seats allotted</dt>
              <dd
                className="text-lg font-semibold"
                data-testid="business-allotment-current"
              >
                {allotted ?? "Not set"}
              </dd>
            </div>
          </dl>
          {allotted !== null && (
            <p
              className={`text-sm mb-2 ${over > 0 ? "text-amber-700" : "text-gray-600"}`}
              data-testid="business-allotment-usage"
            >
              {s.active_comp_either} comp member
              {s.active_comp_either === 1 ? "" : "s"} (either rule) against{" "}
              {allotted} allotted
              {over > 0 ? ` · ${over} over the allotment — review` : ""}.
            </p>
          )}
          <p className="text-xs text-gray-500 mb-3">
            Which members count as “comp” is not decided yet, so both counts are
            shown.
          </p>

          {canSetAllotment && !editing && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setEditing(true)}
            >
              {allotted === null ? "Set comp allotment" : "Change allotment"}
            </Button>
          )}
          {editing && (
            <div className="mt-2 rounded border border-gray-200 p-3 grid gap-2 sm:grid-cols-3">
              <label className="text-sm">
                <span className="block text-gray-700 mb-1">Comp seats</span>
                <input
                  className="w-full rounded border-gray-300 text-sm"
                  inputMode="numeric"
                  value={seats}
                  onChange={(e) => setSeats(e.target.value)}
                  data-testid="allotment-seats"
                />
              </label>
              <label className="text-sm">
                <span className="block text-gray-700 mb-1">Effective from</span>
                <input
                  type="date"
                  className="w-full rounded border-gray-300 text-sm"
                  value={effective}
                  onChange={(e) => setEffective(e.target.value)}
                />
              </label>
              <label className="text-sm sm:col-span-3">
                <span className="block text-gray-700 mb-1">
                  Reason (who approved it, contract, ticket…)
                </span>
                <input
                  className="w-full rounded border-gray-300 text-sm"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  data-testid="allotment-reason"
                />
              </label>
              {err && (
                <p className="text-sm text-red-600 sm:col-span-3">{err}</p>
              )}
              <div className="flex gap-2 sm:col-span-3">
                <Button
                  variant="primary"
                  size="sm"
                  disabled={
                    !seatsValid || reason.trim().length < 3 || save.isPending
                  }
                  onClick={() => save.mutate()}
                >
                  Save
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    setEditing(false);
                    setErr(null);
                  }}
                >
                  Cancel
                </Button>
              </div>
              <p className="text-xs text-gray-500 sm:col-span-3">
                Saving adds a new entry; earlier entries stay in the history.
              </p>
            </div>
          )}

          <div className="mt-3 flex flex-wrap gap-4">
            <button
              type="button"
              className="text-sm text-primary-600 hover:underline"
              onClick={() => setShowMembers((v) => !v)}
            >
              {showMembers ? "Hide members" : "Show members"}
            </button>
            {s.history.length > 0 && (
              <button
                type="button"
                className="text-sm text-primary-600 hover:underline"
                onClick={() => setShowHistory((v) => !v)}
              >
                {showHistory
                  ? "Hide allotment history"
                  : `Allotment history (${s.history.length})`}
              </button>
            )}
          </div>
          {showHistory && (
            <ul
              className="mt-2 divide-y text-sm"
              data-testid="business-allotment-history"
            >
              {s.history.map((h, i) => (
                <li key={i} className="py-1.5">
                  <span className="font-medium">{h.seats} seats</span> from{" "}
                  {formatDateMDY(h.effective_from)}
                  <span className="text-gray-500">
                    {" "}
                    · {h.reason} · {h.entered_by || "unknown"},{" "}
                    {formatDateMDY(h.entered_at)}
                  </span>
                </li>
              ))}
            </ul>
          )}
          {showMembers && (
            <div className="mt-2" data-testid="business-members-list">
              {members.isLoading && (
                <p className="text-sm text-gray-500">Loading…</p>
              )}
              {members.data && members.data.length === 0 && (
                <p className="text-sm text-gray-500">No member accounts.</p>
              )}
              {members.data && members.data.length > 0 && (
                <ul className="divide-y text-sm">
                  {members.data.map((m) => (
                    <li
                      key={m.zuora_account_id}
                      className="py-1.5 flex flex-wrap items-center justify-between gap-2"
                    >
                      <span>
                        {m.contact_id ? (
                          <Link
                            to={`/contacts/${m.contact_id}`}
                            className="text-primary-600 hover:underline"
                          >
                            {m.contact_name || m.account_number || "Member"}
                          </Link>
                        ) : (
                          <span>{m.account_number || m.zuora_account_id}</span>
                        )}
                        {m.account_number && (
                          <span className="ml-2 text-xs text-gray-500">
                            {m.account_number}
                          </span>
                        )}
                      </span>
                      <span className="text-xs text-gray-600">
                        {m.active ? "Active" : "Not active"}
                        {m.account_type ? ` · ${m.account_type}` : ""}
                        {m.mrr === 0 ? " · $0" : ""}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </>
      )}
    </Card>
  );
};

export default BusinessCompAllotmentPanel;
